import { useEffect, useState } from "react";
import { listAdminQueue, getAdminStats } from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";

const STATUS_LABELS = {
  received: { label: "신규", tone: "new" },
  analyzing: { label: "분석중", tone: "work" },
  research: { label: "리서치중", tone: "work" },
  reviewing: { label: "검토 대기", tone: "review" },
  done: { label: "전송 완료", tone: "done" },
  cancelled: { label: "취소됨", tone: "done" },
};

const TONE_COLORS = {
  new: ["#e74c3c22", "#c0392b"],
  work: ["#ff993322", "#e6852b"],
  review: ["#2c5f8d22", "#2c5f8d"],
  done: ["#27ae6022", "#1f7a3f"],
};

function formatAge(birthMonth) {
  if (!birthMonth) return "";
  const b = new Date(birthMonth);
  const n = new Date();
  const months =
    (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
  if (months < 12) return `${Math.max(0, months)}개월`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
}

function timeAgo(iso) {
  if (!iso) return "-";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "방금 전";
  if (mins < 60) return `${mins}분 전`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}시간 전`;
  const days = Math.floor(hrs / 24);
  return `${days}일 전`;
}

function shortId(id) {
  return id ? "C-" + id.slice(0, 6).toUpperCase() : "";
}

export default function AdminDashboard({ onOpen, onSignOut }) {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [q, s] = await Promise.all([listAdminQueue(), getAdminStats()]);
      setQueue(q);
      setStats(s);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const renderStatus = (status) => {
    const info = STATUS_LABELS[status] ?? { label: status, tone: "work" };
    const [bg, fg] = TONE_COLORS[info.tone];
    return (
      <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:bg,color:fg}}>
        {info.label}
      </span>
    );
  };

  const newToday = stats?.newToday ?? 0;
  const inProgress = stats?.inProgress ?? 0;
  const reviewing = stats?.reviewing ?? 0;
  const doneThisWeek = stats?.doneThisWeek ?? 0;

  const activeCount = queue.filter((c) => c.status !== "done" && c.status !== "cancelled").length;
  const queueBadge = activeCount > 0 ? (
    <span style={{marginLeft:'auto',background:'#ff9933',color:'#fff',fontSize:10,padding:'2px 7px',borderRadius:9999,fontWeight:800}}>
      {activeCount}
    </span>
  ) : null;

  return (
    <div className="admin">
      <AdminSidebar active="queue" onSignOut={onSignOut} extraBadge={queueBadge}/>

      <main className="admin-main">
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:6}}>
          <div>
            <h1 className="admin-h1">분석 큐</h1>
            <p className="admin-sub">
              {loading
                ? "불러오는 중..."
                : `오늘 ${newToday}건 · 분석 진행 중 ${inProgress}건 · 검토 대기 ${reviewing}건`}
            </p>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-outline" style={{padding:'8px 16px',fontSize:12}} onClick={load}>
              ⟳ 새로고침
            </button>
          </div>
        </div>

        <div className="admin-stats">
          {[
            { l: "오늘 신규 접수", v: newToday, d: "status=received", tone: "#2c5f8d" },
            { l: "분석 진행 중", v: inProgress, d: "analyzing + research", tone: "#e6852b" },
            { l: "검토 대기", v: reviewing, d: "status=reviewing", tone: "#6c5ce7" },
            { l: "이번 주 완료", v: doneThisWeek, d: "최근 7일", tone: "#1f7a3f" },
          ].map((s, i) => (
            <div key={i} style={{background:'#fff',borderRadius:14,padding:'16px 18px',boxShadow:'var(--shadow-sm)'}}>
              <div style={{fontSize:11,color:'#718096',fontWeight:700,textTransform:'uppercase',letterSpacing:.4}}>{s.l}</div>
              <div style={{fontSize:28,fontWeight:800,color:s.tone,marginTop:4,letterSpacing:-0.5,fontVariantNumeric:'tabular-nums'}}>
                {loading ? "—" : s.v}
              </div>
              <div style={{fontSize:11,color:'#718096',marginTop:2}}>{s.d}</div>
            </div>
          ))}
        </div>

        {error && (
          <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'12px 14px',marginBottom:16,fontSize:13,color:'#c0392b'}}>
            큐를 불러오지 못했어요: {error}
          </div>
        )}

        <div className="admin-table-wrap">
          <div className="queue-row head">
            <div>#</div>
            <div>케이스 / 아이</div>
            <div>증상</div>
            <div>영상</div>
            <div>접수</div>
            <div>상태</div>
            <div></div>
          </div>
          {loading ? (
            <div className="queue-row" style={{justifyContent:'center',color:'#718096'}}>
              <div style={{gridColumn:'1 / -1',textAlign:'center',padding:'20px 0'}}>
                <div className="spinner" style={{display:'inline-block'}}/>
              </div>
            </div>
          ) : queue.length === 0 ? (
            <div className="queue-row">
              <div style={{gridColumn:'1 / -1',textAlign:'center',padding:'40px 0',color:'#718096',fontSize:13}}>
                아직 접수된 케이스가 없어요.
              </div>
            </div>
          ) : (
            queue.map((c) => {
              const age = formatAge(c.child?.birth_month);
              const concern = c.concern_tags?.[0] ?? "(증상 미입력)";
              const isNew = c.status === "received";
              return (
                <div key={c.id} className="queue-row" style={{cursor:'pointer'}} onClick={() => onOpen(c.id)}>
                  <div style={{fontSize:11,color:'#718096',fontVariantNumeric:'tabular-nums'}}>{shortId(c.id)}</div>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,display:'flex',alignItems:'center',gap:6}}>
                      {c.child?.name ?? "(이름 없음)"}
                      {age && <span style={{color:'#718096',fontWeight:500}}> · {age}</span>}
                      {isNew && <span style={{fontSize:10,color:'#e74c3c',fontWeight:800,marginLeft:4}}>● 신규</span>}
                    </div>
                  </div>
                  <div style={{color:'#4a5568'}}>{concern}</div>
                  <div style={{fontWeight:700,fontVariantNumeric:'tabular-nums'}}>🎬 {c.video_count}</div>
                  <div style={{color:'#718096'}}>{timeAgo(c.submitted_at ?? c.created_at)}</div>
                  <div>{renderStatus(c.status)}</div>
                  <div style={{color:'#2c5f8d',fontSize:14}}>→</div>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
